/**
 * SignatoryCard — Paramètres › Cabinet : comptable responsable (nom, titre) + image de signature.
 * Imprimés sur la page titre des états financiers complets (Tenue de livres › Rapports). Batch #14.321.
 * La signature est un fichier chiffré réservé aux admins : l'aperçu est chargé avec le jeton (blob), jamais par URL publique.
 */
import { useEffect, useState } from "react";
import { PenLine, Save, Trash2, Upload } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

export const SIGNATORY_FIELDS = ["signatory_name", "signatory_title"];

export default function SignatoryCard({ settings, update, onSave, setSettings }) {
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");

  useEffect(() => {
    let url = "";
    if (!settings.signature_url) { setPreview(""); return undefined; }
    api.get(settings.signature_url.replace(/^\/api/, ""), { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setPreview(url); })
      .catch(() => setPreview(""));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [settings.signature_url, settings._sig_ts]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/settings/signature", fd);
      setSettings((prev) => ({ ...prev, signature_url: r.data.signature_url, _sig_ts: Date.now() }));
      toast.success("Signature enregistrée — elle apparaîtra sur la page titre des états financiers");
    } catch (err) { toast.error(err.response?.data?.detail || "Téléversement impossible"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Retirer l'image de signature ?")) return;
    setBusy(true);
    try { await api.delete("/settings/signature"); setSettings((prev) => ({ ...prev, signature_url: "" })); toast.success("Signature retirée"); }
    catch (err) { toast.error(err.response?.data?.detail || "Suppression impossible"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border rounded-xl p-5 space-y-4" data-testid="signatory-card">
      <h3 className="font-semibold text-sm flex items-center gap-2"><PenLine className="w-4 h-4" /> Comptable responsable — signature des états financiers</h3>
      <p className="text-xs text-slate-500">Nom, titre et signature imprimés dans le bloc « Préparé par » de la page titre des états financiers complets, avec la date du jour.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Nom du comptable responsable</label>
          <input value={settings.signatory_name || ""} onChange={(e) => update("signatory_name", e.target.value)} placeholder="Prénom Nom" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-signatory-name" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Titre</label>
          <input value={settings.signatory_title || ""} onChange={(e) => update("signatory_title", e.target.value)} placeholder="CPA, comptable responsable du dossier" className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="settings-signatory-title" />
        </div>
      </div>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-48 h-20 border-2 border-dashed rounded-xl flex items-center justify-center bg-slate-50 overflow-hidden" data-testid="signature-preview">
          {preview ? <img src={preview} alt="Signature" className="max-w-full max-h-full object-contain p-1" data-testid="signature-img" /> : <span className="text-[11px] text-slate-400 text-center px-2">Aucune signature<br />(facultatif)</span>}
        </div>
        <label className="border px-3 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2">
          <Upload className="w-4 h-4" /> {busy ? "…" : settings.signature_url ? "Remplacer la signature" : "Téléverser la signature (PNG/JPG)"}
          <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={upload} disabled={busy} data-testid="signature-upload" />
        </label>
        {settings.signature_url && <button type="button" onClick={remove} disabled={busy} className="text-sm text-rose-600 hover:underline flex items-center gap-1" data-testid="signature-remove"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>}
      </div>
      <button onClick={() => onSave(SIGNATORY_FIELDS)} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2" data-testid="settings-save-signatory">
        <Save className="w-4 h-4" /> Sauvegarder
      </button>
    </div>
  );
}
